import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from 'recharts';
import { getFuelLabel, FUEL_DEFINITIONS } from '../domain/definitions';

interface GenerationValues {
  Historical?: number;
  Projected?: number;
  Difference?: number;
  [key: string]: number | string | undefined;
}

interface GenerationChangeChartProps {
  generationData: Record<string, GenerationValues>;
  profileLabel?: string;
}

const FUEL_COLORS: Record<string, string> = {
  SUN: '#FBBF24', WND: '#14B8A6', COL: '#6B7280', NG: '#F97316',
  NUC: '#A855F7', WAT: '#3B82F6', OIL: '#92400E', OTH: '#D1D5DB',
  Solar: '#FBBF24', Wind: '#14B8A6', Coal: '#6B7280',
  'Natural Gas': '#F97316', Nuclear: '#A855F7', Hydro: '#3B82F6',
  Water: '#3B82F6', Oil: '#92400E', Other: '#D1D5DB', Petroleum: '#92400E',
};

function getFuelColor(fuelType: string): string {
  if (FUEL_COLORS[fuelType]) return FUEL_COLORS[fuelType];
  const key = Object.keys(FUEL_COLORS).find(
    (k) => fuelType.toLowerCase().includes(k.toLowerCase())
  );
  return key ? FUEL_COLORS[key] : '#9CA3AF';
}

// Custom tooltip with larger font and fuel description
function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  const rawFuel = entry.rawFuel || entry.fuelType;
  const def = FUEL_DEFINITIONS[rawFuel];
  const value = entry.Difference;
  const sign = value >= 0 ? '+' : '';
  const display = Math.abs(value) >= 1
    ? `${sign}${value.toFixed(1)} GWh`
    : `${sign}${(value * 1000).toFixed(0)} MWh`;

  return (
    <div className="rounded-lg bg-surface-600 border border-border shadow-lg px-3 py-2 max-w-[220px]">
      <p className="text-sm font-medium text-text-primary">{getFuelLabel(rawFuel)}</p>
      <p className="text-sm text-quantum-400 font-semibold">{display}</p>
      {def && (
        <p className="text-[11px] text-text-muted mt-1 leading-snug">{def.description}</p>
      )}
    </div>
  );
}

export function GenerationChangeChart({ generationData, profileLabel }: GenerationChangeChartProps) {
  const chartData = useMemo(() => {
    return Object.entries(generationData)
      .map(([fuelType, values]) => ({
        fuelType: getFuelLabel(fuelType),
        Difference: Number(values.Difference) || 0,
        rawFuel: fuelType,
      }))
      .filter((d) => Math.abs(d.Difference) > 0.001)
      .sort((a, b) => b.Difference - a.Difference);
  }, [generationData]);

  if (chartData.length === 0) return null;

  const formatGwh = (v: number): string => {
    if (v === 0) return '0';
    const sign = v >= 0 ? '+' : '';
    if (Math.abs(v) >= 1) return `${sign}${Math.round(v)}`;
    return `${sign}${(v * 1000).toFixed(0)} MWh`;
  };

  return (
    <div className="mt-6 rounded-xl bg-surface-700 border border-border p-6">
      <h3 className="text-lg font-semibold text-text-primary mb-1">
        Generation Displacement
      </h3>
      <p className="text-xs text-text-secondary mb-4">
        Change in grid generation by fuel type with this project (GWh)
      </p>

      <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 40 + 40)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={{ stroke: '#334155' }}
            tickFormatter={(v) => formatGwh(v)}
            label={{ value: profileLabel ? `GWh ${profileLabel}` : 'GWh', position: 'insideBottomRight', offset: -5, style: { fill: '#64748b', fontSize: 11 } }}
          />
          <YAxis
            type="category"
            dataKey="fuelType"
            tick={{ fontSize: 12, fill: '#cbd5e1' }}
            tickLine={false}
            axisLine={false}
            width={110}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148,163,184,0.08)' }} />
          <ReferenceLine x={0} stroke="#64748b" strokeWidth={1} />
          <Bar dataKey="Difference" barSize={20} radius={[0, 3, 3, 0]}>
            {chartData.map((entry) => (
              <Cell
                key={entry.rawFuel}
                fill={getFuelColor(entry.rawFuel)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
